"use client";

import { useState } from "react";
import { Mail, MailOpen, ChevronDown, ChevronUp } from "lucide-react";

/** Turn plain-text URLs into clickable links. App URLs (e.g. /schedule/) become relative paths to avoid ngrok interstitial. */
function Linkify({ text }: { text: string }) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return (
    <>
      {parts.map((part, i) => {
        if (!urlRegex.test(part)) return <span key={i}>{part}</span>;
        let href = part;
        let displayText = part;
        try {
          const url = new URL(part);
          const appPaths = ["/schedule/", "/robo-advisor/"];
          if (appPaths.some((p) => url.pathname.startsWith(p))) {
            href = url.pathname + url.search + url.hash;
            displayText = href;
          }
        } catch { /* keep original href */ }
        return (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            {displayText}
          </a>
        );
      })}
    </>
  );
}

interface EmailBlockProps {
  entryType: "email_sent" | "email_received";
  subject: string | null;
  body: string | null;
  status: string;
  createdAt: string;
  userName: string | null;
}

export function EmailBlock({ entryType, subject, body, status: _status, createdAt, userName }: EmailBlockProps) {
  const [expanded, setExpanded] = useState(false);
  const isSent = entryType === "email_sent";
  const Icon = isSent ? Mail : MailOpen;
  const containerClass = isSent
    ? "border-x-4 border-indigo-500 bg-white"
    : "bg-indigo-50";

  return (
    <div className={`flex ${isSent ? "justify-end" : "justify-start"}`}>
      <div className={`min-w-[85%] max-w-[85%] rounded-lg border border-gray-100 p-3 ${containerClass}`}>
        <div className="flex items-start gap-2">
          <div className="rounded-full bg-indigo-100 p-1.5 text-indigo-600">
            <Icon className="h-3.5 w-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-900">
                Email {isSent ? "Sent" : "Received"}
              </p>
              <span className="text-[10px] text-gray-400">
                {new Date(createdAt).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
            {userName && isSent && <p className="text-[10px] text-gray-500">by {userName}</p>}
            {subject && <p className="mt-1 text-xs font-medium text-gray-800">{subject}</p>}
            {body && (
              <>
                <p className={`mt-1 text-xs text-gray-600 break-words whitespace-pre-wrap ${expanded ? "" : "line-clamp-2"}`}><Linkify text={body} /></p>
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="mt-1 flex items-center gap-0.5 text-[10px] text-indigo-600 hover:text-indigo-800"
                >
                  {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  {expanded ? "Less" : "More"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
